import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DomSanitizer } from '@angular/platform-browser';
import moment from 'moment';
import { finalize, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { type AdditionalOutflowsModel } from 'src/app/models/additional-outflows.model';
import { AdditionalOutflowsService } from 'src/app/services/api/additional-outflows.service';
import { SalesService } from 'src/app/services/api/sales.service';
import { isAdditionalOutflowModel } from 'src/app/utils';
import { environment } from 'src/environments/environment';
import { NotificationService } from 'src/shared/notification.service';
import { DialogAdditionalOutflowComponent } from './dialog-additional-outflow/dialog-additional-outflow.component';
import { DialogCreateAdditionalOutflowComponent } from './dialog-create-additional-outflow/dialog-create-additional-outflow.component';
import { DialogFileDetailComponent } from './dialog-file-detail/dialog-file-detail.component';
import { FileWithServicesAndMenu } from './models';

const SELECTED_FILE_FILTERS = {
  SIN_FILE: 'SIN FILE',
  PROGRAMADO: 'PROGRAMADO',
  RECONFIRMADO: 'RECONFIRMADO',
  OPERADO: 'OPERADO',
  EN_OPERACION: 'EN OPERACION',
};

const AYB_S3_ROUTE = `${environment.alimentosBebidasApi}/api/v1`;
@Component({
  selector: 'app-egreso',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './egreso.component.html',
  styleUrls: ['./egreso.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
})
export class EgresoComponent implements OnInit, OnDestroy {
  private _unsubscribeOnDestroy$: Subject<boolean> = new Subject<boolean>();
  dataSource = new MatTableDataSource<
    FileWithServicesAndMenu | AdditionalOutflowsModel
  >();

  displayedColumns: string[] = [
    'fileNumber',
    'fileDisplayState',
    'dateRange',
    'lstPaX',
    'egresado',
    'restriccionesAlimenticias',
    'detalleFile',
    'expand',
  ];

  isLoading = false;
  additionalOutflows: AdditionalOutflowsModel[] = [];
  filesWithServicesAndMenu: FileWithServicesAndMenu[] = [];
  filesWithServicesAndMenuAndAdditionalOutflows: (
    | FileWithServicesAndMenu
    | AdditionalOutflowsModel
  )[] = [];
  filterSelectedFileNumber: string | null = null;
  filterSelectedEstado: string | null = null;
  filterSelectedOutflowType: string | null = null;
  filterStartDate: Date | any = null;
  filterEndDate: Date | any = null;

  fileNumbers: string[] = [];
  selectableFileStatuses: string[] = Object.values(SELECTED_FILE_FILTERS);
  selectableAdditionalOutflowsTypes: string[] = [
    'Salida de productos por vencimiento',
    'Salida de producto por deterioro',
    'Salida de producto por consumo',
    'Salida de producto por otro motivo',
  ];
  currentExpandedFile: FileWithServicesAndMenu | null = null;

  paginator!: MatPaginator;
  sort!: MatSort;

  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
    this.paginator = paginator;
    if (this.paginator) {
      this.dataSource.paginator = paginator;
    }
  }
  @ViewChild(MatSort, { static: false }) set matSort(sort: MatSort) {
    this.sort = sort;
    if (this.sort) {
      this.dataSource.sort = sort;
    }
  }

  constructor(
    private sanitizer: DomSanitizer,
    public dialog: MatDialog,
    private readonly additionalOutflowsService: AdditionalOutflowsService,
    private readonly salesService: SalesService,
    private readonly notificationService: NotificationService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly httpClient: HttpClient,
  ) {}

  ngOnInit() {
    this.isLoading = true;

    Promise.allSettled([
      firstValueFrom(this.additionalOutflowsService.getAll()),
      this.getAllFilesWithServicesAndMenu(),
    ])
      .then((results) => {
        const [additionalOutflowsResult, filesWithServicesAndMenuResult] =
          results;

        let additionalOutflows: AdditionalOutflowsModel[] = [];
        let filesWithServicesAndMenu: FileWithServicesAndMenu[] = [];

        if (additionalOutflowsResult.status === 'fulfilled') {
          additionalOutflows =
            additionalOutflowsResult.value.getAllAdditionalOutflows
              ?.additionalOutflows || [];
        } else {
          console.error(
            'Error al obtener egresos adicionales:',
            additionalOutflowsResult.reason,
          );
          this.notificationService.notifyError(
            'Hubo un problema al obtener los EGRESOS ADICIONALES. Por favor, NOTIFIQUE sobre este error.',
          );
        }

        if (filesWithServicesAndMenuResult.status === 'fulfilled') {
          filesWithServicesAndMenu = filesWithServicesAndMenuResult.value;
        } else {
          console.error(
            'Error al obtener archivos con servicios y menú:',
            filesWithServicesAndMenuResult.reason,
          );
          this.notificationService.notifyError(
            'Hubo un problema al obtener FILES. Por favor, NOTIFIQUE sobre este error.',
          );
        }

        this.handleAllFiles(filesWithServicesAndMenu);
        this.additionalOutflows = additionalOutflows;

        const filesWithServicesAndMenuAndAdditionalOutflows = [
          ...this.filesWithServicesAndMenu.map((file) => ({
            ...file,
            type: 'FILE_OUTFLOWS',
            createdAt: file?.fechaInicio ?? new Date().toISOString(),
          })),
          ...this.additionalOutflows.map((outflow) => ({
            ...outflow,
            type: 'ADDITIONAL_OUTFLOW',
            createdAt: outflow?.createdAt ?? new Date().toISOString(),
          })),
        ].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

        this.dataSource.data = filesWithServicesAndMenuAndAdditionalOutflows;
        this.filesWithServicesAndMenuAndAdditionalOutflows =
          filesWithServicesAndMenuAndAdditionalOutflows;
        this.applyFilterSelect();
      })
      .catch((error) => {
        console.error('Error inesperado:', error);
        this.notificationService.notifyError(
          'Hubo un error inesperado al obtener los datos. Por favor, NOTIFIQUE sobre este error.',
        );
      })
      .finally(() => {
        this.isLoading = false;
        this.cdRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeOnDestroy$.next(true);
    this._unsubscribeOnDestroy$.complete();
  }

  private _sortFilesAndOutflows({
    newOutflow = null,
  }: {
    newOutflow: FileWithServicesAndMenu | AdditionalOutflowsModel | null;
  }) {
    if (newOutflow != null && isAdditionalOutflowModel(newOutflow)) {
      this.additionalOutflows.push(newOutflow);
    }

    if (newOutflow != null && !isAdditionalOutflowModel(newOutflow)) {
      this.filesWithServicesAndMenu.push(newOutflow);
    }

    if (newOutflow != null) {
      this.filesWithServicesAndMenuAndAdditionalOutflows.push(newOutflow);
    }

    const files = this.filesWithServicesAndMenu;
    const additionalOutflows = this.additionalOutflows;
    let filesWithServicesAndMenuAndAdditionalOutflows =
      this.filesWithServicesAndMenuAndAdditionalOutflows;

    filesWithServicesAndMenuAndAdditionalOutflows = [
      ...files.map((file) => ({
        ...file,
        createdAt: file?.fechaInicio ?? new Date().toISOString(),
      })),
      ...additionalOutflows.map((outflow) => ({
        ...outflow,
        createdAt: outflow?.createdAt ?? new Date().toISOString(),
      })),
    ].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    this.filesWithServicesAndMenuAndAdditionalOutflows =
      filesWithServicesAndMenuAndAdditionalOutflows;

    this.applyFilterSelect();
  }

  handleAllFiles(filesAndMenu: FileWithServicesAndMenu[]) {
    this.filesWithServicesAndMenu = filesAndMenu;

    for (const file of this.filesWithServicesAndMenu) {
      const partes = file.dateRange?.split('-') ?? [];
      file.fechaInicio = moment(partes[0], 'DD/MM/YYYY').toDate();
      file.fechaFin = moment(partes[1], 'DD/MM/YYYY').toDate();

      const cantPAX = file.lstPaX.length;
      const cantTC = file.tcQuantity ?? 0;

      const formattedServices = [];

      for (const services of file.services) {
        let contChofer = 0;
        let contGuia = 0;
        let contPAX = 0;
        let contTC = 0;

        let totalesPersonalCampoAYB = services.totalesPersonalCampoAYB ?? 0;
        let totalMenuXFilaAYB = 0;

        const nombreServicio = services.nombreServicio?.toLowerCase() ?? '';
        const nroPersonasPax = services.nroPersonasPax ?? [];
        if (nombreServicio.includes('chofer')) {
          const found = nroPersonasPax.find(
            (n: any) => Number(n.id?.split('-')[1] ?? '') === cantPAX,
          );
          contChofer = found
            ? (found.numPax ?? 0)
            : (nroPersonasPax[nroPersonasPax.length - 1]?.numPax ?? 0);
        } else if (nombreServicio.includes('guia')) {
          const found = nroPersonasPax.find(
            (n: any) => Number(n.id?.split('-')[1] ?? '') === cantPAX,
          );
          contGuia = found
            ? (found.numPax ?? 0)
            : (nroPersonasPax[nroPersonasPax.length - 1]?.numPax ?? 0);
        } else if (nombreServicio.includes('t.c.')) {
          contTC = cantTC;
        } else {
          contPAX = cantPAX;
        }

        if (totalMenuXFilaAYB === 0) {
          totalMenuXFilaAYB =
            Number(totalesPersonalCampoAYB) +
            Number(contPAX) +
            Number(contTC) +
            Number(contChofer) +
            Number(contGuia);
        }
        formattedServices.push({
          ...services,
          totalesPersonalCampoAYB,
          totalMenuXFilaAYB,
          day: services.day,
          cantPAX: contPAX,
          tc: contTC,
          totalChoferAYB: contChofer,
          totalGuiaAYB: contGuia,
        });
      }

      file.services = formattedServices;

      file.services.sort((a: any, b: any) => {
        const fechaA = moment(a.fechaServicio, 'DD/MM/YYYY').toDate();
        const fechaB = moment(b.fechaServicio, 'DD/MM/YYYY').toDate();
        return fechaA.getTime() - fechaB.getTime();
      });
      for (let i = 0; i < file.services.length; i++) {
        const current = file.services[i];
        const next = file.services[i + 1];

        if (next && current.day !== next.day) {
          current.nextIsDifferent = true;
        } else {
          current.nextIsDifferent = false;
        }
      }
    }

    this.filesWithServicesAndMenu.sort(
      (a: any, b: any) => a.fechaInicio - b.fechaInicio,
    );

    const fileNumbers = this.filesWithServicesAndMenu
      .filter((item: any) => item.fileNumber)
      .map((item: any) => item.fileNumber);

    this.fileNumbers = [...new Set(fileNumbers)];
    return this.filesWithServicesAndMenu;
  }

  async getAllFilesWithServicesAndMenu(): Promise<FileWithServicesAndMenu[]> {
    try {
      let LastEvaluatedKey = null;
      let hasMore = true;
      let allFiles: FileWithServicesAndMenu[] = [];
      while (hasMore) {
        const queryGetAllFilesWithServicesAndMenuResponse: any =
          await firstValueFrom(
            this.salesService
              .queryGetAllFilesWithServicesAndMenu(
                'ALIMENTOSYBEBIDAS',
                LastEvaluatedKey,
              )
              .pipe(takeUntil(this._unsubscribeOnDestroy$)),
          );

        hasMore =
          queryGetAllFilesWithServicesAndMenuResponse
            ?.getAllFilesWithServicesAndMenu?.isThereMore;

        const resultFiles: FileWithServicesAndMenu[] =
          queryGetAllFilesWithServicesAndMenuResponse
            ?.getAllFilesWithServicesAndMenu?.resultFiles;

        allFiles = [...allFiles, ...resultFiles];
        LastEvaluatedKey =
          queryGetAllFilesWithServicesAndMenuResponse
            ?.getAllFilesWithServicesAndMenu?.LastEvaluatedKey;
      }

      if (allFiles?.length === 0) {
        throw new Error('There is no files with services and menu');
      }

      return allFiles.filter((file) => {
        const estadoFile = file?.estadoFile?.toUpperCase() ?? '';
        return [
          'RECONFIRMADO',
          'PROGRAMADO',
          'EN OPERACION',
          'OPERADO',
          'CONFIRMADO',
          'CERRADO',
        ].includes(estadoFile);
      });
    } catch (error) {
      console.error('Error ', error);
      return [];
    }
  }

  obtenerArchivoConSubscribe(id: string) {
    return new Promise((resolve) => {
      this.httpClient
        .get(`${AYB_S3_ROUTE}/getFileOrdenSalida/${id}`)
        .subscribe({
          next: (res: any) => {
            let binary = '';
            let bytes = new Uint8Array(res.data);
            let len = bytes.byteLength;
            for (let i = 0; i < len; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            const archivo =
              this.sanitizer.bypassSecurityTrustResourceUrl(binary);
            resolve(archivo);
          },
          error: () => {
            resolve(null);
          },
        });
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  /**
   * Applies selected filters (file number, status, outflow type, start date, end date)
   * to the combined list of outflows and updates the table data source.
   *
   * @param None
   * @returns {void} Updates this.dataSource.data in place.
   * @throws {Error} No exceptions thrown directly.
   */
  applyFilterSelect() {
    let filteredOutflows = this.filesWithServicesAndMenuAndAdditionalOutflows;

    if (this.filterSelectedFileNumber) {
      filteredOutflows = filteredOutflows?.filter((outflow) => {
        if (!isAdditionalOutflowModel(outflow)) {
          return outflow?.fileNumber != null &&
            this.filterSelectedFileNumber != null
            ? outflow.fileNumber === this.filterSelectedFileNumber
            : false;
        }
        return false;
      });
    }
    if (this.filterSelectedEstado) {
      filteredOutflows = filteredOutflows?.filter((outflow) => {
        if (isAdditionalOutflowModel(outflow)) {
          return this.filterSelectedEstado === SELECTED_FILE_FILTERS.SIN_FILE;
        }
        return outflow.fileDisplayState != null &&
          this.filterSelectedEstado != null
          ? outflow.fileDisplayState === this.filterSelectedEstado
          : false;
      });
    }
    if (this.filterSelectedOutflowType) {
      filteredOutflows = filteredOutflows?.filter((outflow) => {
        if (isAdditionalOutflowModel(outflow)) {
          return this.filterSelectedOutflowType === outflow.outflowType;
        }
        return false;
      });
    }

    if (this.filterStartDate) {
      filteredOutflows = filteredOutflows?.filter((outflow) => {
        if (isAdditionalOutflowModel(outflow)) {
          return outflow.outflowDate === this.filterStartDate;
        }
        return (
          moment(outflow.fechaInicio, 'DD.MM.YYYY').toDate() >=
          moment(this.filterStartDate, 'DD.MM.YYYY').toDate()
        );
      });
    }

    if (this.filterStartDate || this.filterEndDate) {
      filteredOutflows = filteredOutflows.filter((fileOrOutflow) => {
        const outflowStartDate = isAdditionalOutflowModel(fileOrOutflow)
          ? moment(fileOrOutflow.outflowDate, 'DD.MM.YYYY').toDate()
          : moment(fileOrOutflow.fechaInicio, 'DD/MM/YYYY').toDate();

        if (outflowStartDate == null) return false;

        const startDate = new Date(this.filterStartDate ?? '');

        if (this.filterStartDate && !this.filterEndDate) {
          return outflowStartDate >= startDate;
        }

        const endDate = new Date(this.filterEndDate ?? '');

        if (!this.filterStartDate && this.filterEndDate) {
          return outflowStartDate <= endDate;
        }

        if (this.filterStartDate && this.filterEndDate) {
          if (isAdditionalOutflowModel(fileOrOutflow)) {
            return outflowStartDate >= startDate && outflowStartDate <= endDate;
          }
          return outflowStartDate >= startDate && outflowStartDate <= endDate;
        }
        return false;
      });
    }

    this.dataSource.data = filteredOutflows;
  }

  cleanDateSelected() {
    this.filterStartDate = null;
    this.filterEndDate = null;
    this.applyFilterSelect();
  }

  openDialogAddAdditionalOutflow() {
    let additionalOutflowDialogRef = this.dialog.open(
      DialogCreateAdditionalOutflowComponent,
      {
        width: '1000px',
        minHeight: '379px',
        maxHeight: '900px',
        data: {
          actionType: 'CREATE_ADDITIONAL_OUTFLOW',
        },
      },
    );

    additionalOutflowDialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe(
        (dataAfterClosed: {
          success: boolean;
          createdOutflow: AdditionalOutflowsModel;
        }) => {
          if (dataAfterClosed?.success && dataAfterClosed.createdOutflow) {
            this._sortFilesAndOutflows({
              newOutflow: dataAfterClosed.createdOutflow,
            });
          }
        },
      );
  }

  openDialogFileDetails(file: FileWithServicesAndMenu) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      file: file,
    };
    dialogConfig.width = '64rem';
    dialogConfig.height = '90%';

    let fileDetailDialogRef = this.dialog.open(
      DialogFileDetailComponent,
      dialogConfig,
    );
  }

  openDialogAddFoodItemToAdditionalOutflow(
    additionalOutflow: AdditionalOutflowsModel,
  ) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      additionalOutflow,
    };
    dialogConfig.panelClass = 'custom-dialog-class';
    dialogConfig.autoFocus = false;

    let dialogRef = this.dialog.open(
      DialogAdditionalOutflowComponent,
      dialogConfig,
    );
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe({
        next: (result: {
          success: boolean;
          additionalOutflowFoodItems: any[];
        }) => {
          // if (result.success) {
          //   this.sortFilesAndOutflows({
          //     newOutflow: {
          //       ...additionalOutflow,
          //       additionalOutflowFoodItems: result.additionalOutflowFoodItems
          //     }
          //   })
          // }
        },
        error: () => {
          console.error('Error al cerrar el diálogo');
        },
      });
  }

  obtenerRestriccionesAlimenticias(
    file: FileWithServicesAndMenu | AdditionalOutflowsModel,
  ): boolean {
    if (isAdditionalOutflowModel(file) || !file?.lstPaX) return false;
    return file.lstPaX.some(
      (passenger) => passenger.passengerId && passenger.estado,
    );
  }
  deleteAdditionalOutflow(additionalOutflow: AdditionalOutflowsModel) {
    this.isLoading = true;

    this.additionalOutflowsService
      .delete({
        additionalOutflowId: additionalOutflow?.id ?? '',
      })
      .pipe(
        takeUntil(this._unsubscribeOnDestroy$),
        finalize(() => {
          this.isLoading = false;
          this.cdRef.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.notificationService.showSuccessAlert(
            'Egreso eliminado correctamente',
          );
          this.additionalOutflows = this.additionalOutflows.filter(
            (outflow) => outflow.id !== additionalOutflow.id,
          );
          this._sortFilesAndOutflows({
            newOutflow: null,
          });
        },
        error: (error) => {
          console.error('Error al eliminar el egreso:', error);
          this.notificationService.notifyError(
            'Hubo un problema al eliminar el egreso. Por favor, NOTIFIQUE sobre este error.',
          );
        },
      });
  }
}
